import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Col, Row } from 'reactstrap';
import Moment from 'react-moment';

import XMTable from '../../components/XMTable';
import XMSearchBox from '../../components/XMSearchBox';
import XMPagination from '../../components/XMPagination';
import XMTooltipButton from '../../components/XMTooltipButton';
import * as appStoreActions from '../../actions/appStoreActions';
import DeleteAdvertModal from './DeleteAdvertModal';
import { AppCell, AdvertBadges, LinkedModuleChip, NoResults } from './rowParts';
import { PAGE_SIZE, searchedFor, sortedBy } from './listQuery';
import { advertInstalledSitesPath } from './installedSitesScope';

const HEADERS = [
  { name: 'App', code: 'name', sortable: true },
  { name: 'Description' },
  { name: 'Last Updated', code: 'last-updated', sortable: true },
  {
    name: 'Install Count',
    code: 'install-count',
    sortable: true,
    style: { width: '8rem' },
    hint: 'Stores that installed THIS advert. Installs of the same app made any other way are not counted here — see All Apps for the app-wide total.'
  },
  { name: 'Actions', style: { width: '7rem' } }
];

/**
 * Server-paged and server-searched (UN-3339): the tab holds one page and its total. Every change of
 * search, sort or page is one query; a new search or sort starts again from the first page.
 */
const ManageAppAdverts = ({ adverts, advertsLoaded, error, deleteError, dispatch }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState({ search: '', page: 0, sortBy: null, direction: null });
  const [toDelete, setToDelete] = useState(null);
  const { search } = query;

  // Every dispatch below is caught: the thunks reject(err) and the reducer already holds the
  // message, so the catch exists to consume the rejection rather than to handle it. Without it
  // each failure is also an unhandled promise rejection.
  useEffect(() => {
    dispatch(appStoreActions.getAdverts({ ...query, size: PAGE_SIZE }))
      .then(page => {
        // A delete can empty the last page; show the one before it rather than an empty table.
        if (page && page.content && page.content.length === 0 && page.number > 0) {
          setQuery(q => ({ ...q, page: page.number - 1 }));
        }
      })
      .catch(() => {});
  }, [dispatch, query]);

  const handleSort = (sort, sortDir) => setQuery(q => sortedBy(q, sort, sortDir));

  // Close the modal only when the delete actually succeeded. Closing on .finally() made a failed
  // delete look exactly like a successful one - the dialog vanished and the row stayed. The page is
  // then read again, so the next advert moves up into the gap.
  const handleDeleteConfirmed = () =>
    dispatch(appStoreActions.deleteAdvert(toDelete.uuid))
      .then(() => {
        setToDelete(null);
        setQuery(q => ({ ...q }));
      })
      .catch(() => {});

  const rows = adverts.content;

  return (
    <div>
      <p className="text-muted">Create and publish app adverts for the commercebuild App Store.</p>
      <Row className="mb-3">
        <Col md="5">
          <XMSearchBox onSearch={({ fuzzyString }) => setQuery(q => searchedFor(q, fuzzyString))} />
        </Col>
        <Col className="text-end">
          <Button color="dark" onClick={() => navigate('/manage-apps/adverts/new')}>
            <i className="icon-plus me-1" /> New Advert
          </Button>
        </Col>
      </Row>

      {error ? (
        <Alert color="danger">{error}</Alert>
      ) : null}

      {advertsLoaded && !error && !search && adverts.totalElements === 0 ? (
        <div className="text-center text-muted py-5">
          <h5>No app adverts yet</h5>
          <p>Create the first advert to publish an app to the commercebuild App Store.</p>
        </div>
      ) : (
        <XMTable headers={HEADERS} handleSort={handleSort}>
          {advertsLoaded && !error && rows.length === 0 ? (
            <NoResults colSpan={HEADERS.length}>
              No adverts match &ldquo;{search}&rdquo;.
            </NoResults>
          ) : null}
          {rows.map(advert => (
            <tr key={advert.uuid}>
              <td className="align-middle">
                <AppCell name={advert.name} subtitle={advert.uuid} imageUrl={advert.imageUrl} />
              </td>
              <td className="align-middle">
                <div>{advert.description}</div>
                <div className="mt-1">
                  <AdvertBadges advert={advert} />
                  <LinkedModuleChip name={advert.appName} />
                </div>
              </td>
              <td className="align-middle">
                <div>
                  <Moment format="YYYY-MM-DD HH:mm">{advert.lastUpdated}</Moment>
                </div>
                {advert.lastUpdatedBy ? (
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    by {advert.lastUpdatedBy}
                  </div>
                ) : null}
              </td>
              <td className="align-middle">
                {advertInstalledSitesPath(advert) ? (
                  <Link to={advertInstalledSitesPath(advert)}>{advert.installCount}</Link>
                ) : (
                  advert.installCount ?? 0
                )}
              </td>
              <td className="align-middle">
                <XMTooltipButton
                  icon="icon-pencil"
                  tooltipTitle="Edit advert"
                  onClick={() => navigate(`/manage-apps/adverts/${advert.uuid}/edit`)}
                />
                <XMTooltipButton
                  icon="icon-trash"
                  tooltipTitle="Delete advert"
                  onClick={() => setToDelete(advert)}
                />
              </td>
            </tr>
          ))}
        </XMTable>
      )}
      <XMPagination pagination={adverts} paginate={page => setQuery(q => ({ ...q, page }))} />

      <DeleteAdvertModal
        advert={toDelete}
        error={deleteError}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
};

export default connect(state => ({
  adverts: state.appStore.adverts,
  advertsLoaded: state.appStore.advertsLoaded,
  error: state.appStore.error,
  deleteError: state.appStore.deleteError
}))(ManageAppAdverts);
